import _map from 'lodash/map';
import _omit from 'lodash/omit';

import {
  ClinchCondition,
  ConditionWizardCategory,
  MultiConditionType,
  ServerAbstractType,
  ServerClinchCondition,
  ServerConditionWizardCategory,
  ServerMultiConditionType,
  ServerSingleConditionType,
  SingleConditionType,
} from './conditions.types';
import {CLEnumKeys, CLEnumsMapper} from 'common/src/modules/enums/enums.types';
import {conditionCategoryData} from './conditions.consts';

export function conditionToClientModel({
  conditionIndex,
  serverCondition,
  campaignPageEnums,
  type,
}: {
  conditionIndex: number;
  serverCondition: ServerClinchCondition;
  campaignPageEnums: Record<string, CLEnumsMapper>;
  type: ServerAbstractType;
}): ClinchCondition | null {
  if (!serverCondition) {
    return null;
  }

  if ((serverCondition as ServerSingleConditionType).condOperator) {
    return singleConditionToClientModel({
      conditionIndex,
      serverSingleCondition: serverCondition as ServerSingleConditionType,
      campaignPageEnums,
      type,
    });
  }

  return {
    $type: type.operator,
    and: (serverCondition as ServerMultiConditionType).and,
    conditionKey: conditionIndex.toString(),
    predicates: _map((serverCondition as ServerMultiConditionType).predicates, (condition, index) =>
      conditionToClientModel({
        conditionIndex: index,
        serverCondition: condition,
        campaignPageEnums,
        type,
      }),
    ).filter((condition): condition is ClinchCondition => condition !== null),
  };
}

function singleConditionToClientModel({
  conditionIndex,
  serverSingleCondition,
  campaignPageEnums,
  type,
}: {
  conditionIndex: number;
  serverSingleCondition: ServerSingleConditionType;
  campaignPageEnums: Record<string, CLEnumsMapper>;
  type: ServerAbstractType;
}): SingleConditionType {
  return {
    conditionKey: conditionIndex.toString(),
    $type: type.condition,
    ...serverSingleCondition,
    conditionUiName:
      serverSingleCondition.condOperator !== null
        ? campaignPageEnums[CLEnumKeys.CampaignPage.PredicateConditionOperator].byValue[
            serverSingleCondition.condOperator
          ].uiName
        : '',
  };
}

export function conditionToServerModel(
  conditions: ClinchCondition,
  type: ServerAbstractType,
): ServerClinchCondition {
  if ((conditions as ServerSingleConditionType).condOperator) {
    return singleConditionToServerModel(conditions as SingleConditionType, type);
  }

  return {
    $type: type.operator,
    and: (conditions as MultiConditionType).and,
    predicates: _map((conditions as MultiConditionType).predicates, (condition) =>
      conditionToServerModel(condition, type),
    ),
  };
}

function singleConditionToServerModel(
  singleCondition: SingleConditionType,
  type: ServerAbstractType,
): ServerSingleConditionType {
  return _omit(
    {
      $type: type.condition,
      ...singleCondition,
    },
    'conditionKey',
  );
}

export function toLocalConditionCategories(
  availableCategories: ServerConditionWizardCategory[] | undefined,
): ConditionWizardCategory[] {
  if (!availableCategories) return [];

  return availableCategories.map((category) => {
    return {
      ...category,
      ...conditionCategoryData[category.value],
      ...(category.customDisplayName ? {title: category.customDisplayName} : {}),
      categories: toLocalConditionCategories(category.categories),
    };
  });
}
